<template>
    <AppLayout title="Codigos postales">
        <div class="cps bg-white">
            <Toast />

            <Breadcrumb
                name="Códigos postales"
                routeName="cps"
                page="Todos los codigos postales"
            />

            <DataTable :value="cps" responsiveLayout="scroll" dataKey="id">
                <template #header>
                    <div
                        class="flex flex-col-reverse items-end lg:justify-between lg:flex-row"
                    >
                        <span class="block mt-2 p-input-icon-left">
                            <i class="pi pi-search" />
                            <InputText
                                placeholder="Buscar..."
                                v-model="filters.search"
                                class="p-inputtext-sm"
                            />
                        </span>
                        <div class="flex">
                            <Link href="/cps/crear">
                                <Button>
                                    <div class="flex items-center space-x-2">
                                        <Icon><Plus /></Icon>
                                        <span> Crear codigo </span>
                                    </div>
                                </Button>
                            </Link>
                            <Button @click="handleExport">
                                <div class="flex items-center space-x-2">
                                    <Icon><FileExport /></Icon>
                                    <span> Exportar </span>
                                </div>
                            </Button>
                        </div>
                    </div>
                </template>
                <template #empty> No se encontraron registros. </template>
                <Column field="cp" header="Codigo Postal"></Column>
                <Column field="localidad" header="Localidad"></Column>
                <Column field="provincia" header="Provincia"></Column>
                <Column header="Aciones">
                    <template #body="cp">
                        <div class="flex space-x-2">
                            <Link :href="'/cps/' + cp.data?.id + '/edit'">
                                <Button>
                                    <Icon><PencilAlt /></Icon>
                                </Button>
                            </Link>
                            <Button
                                variant="destructive"
                                @click="confirmDelete(cp.data)"
                            >
                                <Icon><Trash /></Icon>
                            </Button>
                        </div>
                    </template>
                </Column>
            </DataTable>
            <div
                class="h-20 w-full flex items-center justify-center overflow-x-auto"
            >
                <FlowbitePaginator :paginationData="paginationData" />
            </div>
            <!-- delete dialog -->
            <Dialog
                v-model:visible="deleteCpDialog"
                :style="{ width: '450px' }"
                header="Confirmar"
                :modal="true"
            >
                <div class="flex items-center justify-center">
                    <i
                        class="pi pi-exclamation-triangle mr-3"
                        style="font-size: 2rem"
                    />
                    <span v-if="cp"
                        >¿Seguro que deseas eliminar a <b>{{ cp.cp }}</b
                        >?</span
                    >
                </div>
                <template #footer>
                    <div class="flex justify-end space-x-2">
                        <Button @click="deleteCpDialog = false"
                            >Cancelar</Button
                        >
                        <Button variant="destructive" @click="deleteCp(cp)"
                            >Eliminar</Button
                        >
                    </div>
                </template>
            </Dialog>
        </div>
    </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout.vue";
import { defineComponent } from "@vue/runtime-core";
import { Link } from "@inertiajs/inertia-vue3";
import { Icon } from "@vicons/utils";
import { Plus, PencilAlt, Trash, FileExport } from "@vicons/fa";
import Breadcrumb from "@/shared/Breadcrumb.vue";
import Button from "@/shared/Button.vue";
import { Inertia } from "@inertiajs/inertia";
import FlowbitePaginator from "./components/FlowbitePaginator.vue";
import pickBy from "lodash/pickBy";
import throttle from "lodash/throttle";
import axios from "axios";

export default defineComponent({
    components: {
        AppLayout,
        Link,
        Icon,
        Plus,
        Breadcrumb,
        Button,
        PencilAlt,
        Trash,
        FlowbitePaginator,
        FileExport,
    },
    props: {
        codigos_postales: Array,
        filtros: Object,
    },
    data() {
        return {
            deleteCpDialog: false,
            cps: [],
            cp: {},
            isLoading: false,
            filters: {
                search: null,
            },
        };
    },
    created() {
        this.cps = [...this.codigos_postales.data];
    },
    computed: {
        paginationData() {
            const { data: _, ...pageData } = this.codigos_postales;
            return pageData;
        },
    },
    watch: {
        filters: {
            deep: true,
            handler: throttle(function () {
                Inertia.get("/cps", pickBy(this.filters), {
                    preserveState: true,
                    onSuccess: () => {
                        this.cps = [...this.codigos_postales.data];
                    },
                    onError: () => {
                        this.$toast.add({
                            severity: "error",
                            summary: "Ha ocurrido un error",
                            life: 3000,
                        });
                    },
                });
            }, 150),
        },
    },
    methods: {
        hideDialog() {
            this.deleteCpDialog = false;
            this.isLoading = false;
        },
        reloadProps() {
            Inertia.reload({ only: ["codigos_postales"] });
            this.cps = [...this.codigos_postales.data];
        },
        handleExport() {
            axios
                .get("/cps/exportar", { responseType: "blob" })
                .then((res) => {
                    let blob = new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    let downloadXls = document.createElement("a");
                    downloadXls.href = window.URL.createObjectURL(blob);
                    downloadXls.download = `listado_codigos_postales.xls`;
                    downloadXls.click();
                })
                .catch((err) => console.error(err));
        },
        confirmDelete(cp) {
            this.cp = cp;
            this.deleteCpDialog = true;
        },
        deleteCp() {
            this.deleteCpDialog = false;
            this.$inertia.form().delete(`/cps/${this.cp.id}`, {
                onSuccess: () => {
                    this.$toast.add({
                        severity: "success",
                        summary: "Exito",
                        detail: "Codigo postal eliminado",
                        life: 3000,
                    });
                    this.reloadProps();
                },
                onError: (errors) => {
                    this.$toast.add({
                        severity: "error",
                        summary: "Ha ocurrido un error",
                        life: 3000,
                    });
                },
            });
        },
    },
});
</script>

<style>
.cps .p-datatable-header {
    background: white;
    border-top: none;
}
.cps .p-inputtext-sm {
    border-radius: 0.5rem;
}
.cps .p-inputtext-sm:hover {
    border-color: #2e5f98;
}
.cps .p-inputtext-sm:enabled:focus {
    border-color: #2e5f98;
    box-shadow: 0 0 0 1px #2e5f98;
}
.cps .p-paginator .p-paginator-pages .p-paginator-page.p-highlight:focus {
    border-color: #2e5f98;
}
.cps .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
    border-color: #2e5f98;
}

.cps .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
    color: #2e5f98;
}
.cps .p-link:focus {
    box-shadow: 0 0 0 1px #2e5f98;
}

.cps .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    color: #2e5f98;
}

.cps .p-dropdown:not(.p-disabled).p-focus {
    box-shadow: 0 0 0 1px #2e5f98;
    border-color: #2e5f98;
}

.cps li.p-dropdown-item.p-highlight,
.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    color: #2e5f98;
}
</style>
